import React from 'react';
import { Box, ButtonBase, CircularProgress, InputBase, Typography } from '@mui/material';
import { ArrowDownward, ArrowUpward, ChevronLeft, ChevronRight, Search } from '@mui/icons-material';
import { bodyFont, ink, surface } from './tokens';

export type SortDirection = 'asc' | 'desc';

export interface Column<Row> {
  key: string;
  label: string;
  /** Right-aligns the column and sorts it numerically. */
  numeric?: boolean;
  sortable?: boolean;
  /** Grid track for this column. Defaults to `minmax(0,1fr)`. */
  width?: string;
  render: (row: Row) => React.ReactNode;
}

/**
 * The table shell shared by every list on the analytics page.
 *
 * MUI's `<Table>` brings its own type scale and divider colours, which is why
 * the old dashboard's tables looked like a different product from the page
 * around them. This is a CSS grid with the dashboard's own tokens instead.
 */
interface DataTableProps<Row> {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row, index: number) => React.Key;
  loading?: boolean;
  emptyMessage: string;
  sortKey?: string;
  sortDirection?: SortDirection;
  onSort?: (key: string) => void;
}

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  loading,
  emptyMessage,
  sortKey,
  sortDirection,
  onSort
}: DataTableProps<Row>) {
  const template = columns.map((column) => column.width ?? 'minmax(0,1fr)').join(' ');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: '56px' }}>
        <CircularProgress size={26} sx={{ color: '#1778A8' }} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box
        sx={{
          py: '44px',
          px: '20px',
          borderRadius: '12px',
          backgroundColor: surface.sunken,
          textAlign: 'center'
        }}
      >
        <Typography sx={{ fontFamily: bodyFont, fontSize: 14, color: ink.muted }}>
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: columns.length > 3 ? 620 : 420 }}>
        <Box
          role='row'
          sx={{
            display: 'grid',
            gridTemplateColumns: template,
            gap: '14px',
            alignItems: 'center',
            pb: '10px',
            borderBottom: `1px solid ${surface.border}`
          }}
        >
          {columns.map((column) => {
            const active = sortKey === column.key;
            const sortable = column.sortable && onSort;
            const content = (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontFamily: bodyFont,
                  fontWeight: 600,
                  fontSize: 11,
                  letterSpacing: '.07em',
                  textTransform: 'uppercase',
                  color: active ? ink.body : ink.faint
                }}
              >
                {column.label}
                {active &&
                  (sortDirection === 'asc' ? (
                    <ArrowUpward sx={{ fontSize: 13 }} />
                  ) : (
                    <ArrowDownward sx={{ fontSize: 13 }} />
                  ))}
              </Box>
            );

            return (
              <Box
                key={column.key}
                sx={{
                  justifySelf: column.numeric ? 'end' : 'start',
                  minWidth: 0
                }}
              >
                {sortable ? (
                  <ButtonBase
                    onClick={() => onSort?.(column.key)}
                    disableRipple
                    aria-label={`Sort by ${column.label}`}
                    sx={{ borderRadius: '6px', '&:hover': { color: ink.body } }}
                  >
                    {content}
                  </ButtonBase>
                ) : (
                  content
                )}
              </Box>
            );
          })}
        </Box>

        {rows.map((row, index) => (
          <Box
            key={rowKey(row, index)}
            role='row'
            sx={{
              display: 'grid',
              gridTemplateColumns: template,
              gap: '14px',
              alignItems: 'center',
              py: '13px',
              borderBottom: `1px solid ${surface.grid}`,
              '&:last-of-type': { borderBottom: 0 }
            }}
          >
            {columns.map((column) => (
              <Box
                key={column.key}
                sx={{
                  justifySelf: column.numeric ? 'end' : 'start',
                  minWidth: 0,
                  fontFamily: bodyFont,
                  fontSize: 14,
                  color: ink.body
                }}
              >
                {column.render(row)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** Search box matching the one on the locations list. */
export const SearchField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => (
  <Box
    sx={{
      width: { xs: '100%', sm: 240 },
      height: 38,
      border: `1px solid ${surface.borderStrong}`,
      borderRadius: '9px',
      backgroundColor: surface.card,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      px: '11px'
    }}
  >
    <Search sx={{ fontSize: 17, color: ink.faint }} />
    <InputBase
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      sx={{
        flex: 1,
        fontFamily: bodyFont,
        fontSize: 14,
        color: ink.strong,
        '& input::placeholder': { color: '#94A2AE', opacity: 1 }
      }}
    />
  </Box>
);

const pagerButtonSx = {
  width: 32,
  height: 32,
  borderRadius: '8px',
  border: `1px solid ${surface.borderStrong}`,
  color: ink.body,
  '&:hover': { backgroundColor: surface.grid },
  '&.Mui-disabled': { color: '#C3CDD6', borderColor: surface.border }
};

/** Page counter and arrows. Hidden entirely when everything fits on one page. */
export const Pager: React.FC<{
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  noun: string;
}> = ({ page, pageSize, total, onPageChange, noun }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        pt: '14px'
      }}
    >
      <Typography sx={{ fontFamily: bodyFont, fontSize: 13, color: ink.faint }}>
        {`${from}–${to} of ${total.toLocaleString()} ${noun}`}
      </Typography>
      {pages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <ButtonBase
            aria-label='Previous page'
            disabled={page === 0}
            onClick={() => onPageChange(page - 1)}
            sx={pagerButtonSx}
          >
            <ChevronLeft sx={{ fontSize: 18 }} />
          </ButtonBase>
          <ButtonBase
            aria-label='Next page'
            disabled={page >= pages - 1}
            onClick={() => onPageChange(page + 1)}
            sx={pagerButtonSx}
          >
            <ChevronRight sx={{ fontSize: 18 }} />
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
};

/** Status chip, matching the subscription pill on the location header. */
export const StatusPill: React.FC<{ label: string; fg: string; bg: string }> = ({
  label,
  fg,
  bg
}) => (
  <Box
    component='span'
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      height: 24,
      px: '9px',
      borderRadius: '99px',
      backgroundColor: bg,
      color: fg,
      fontFamily: bodyFont,
      fontWeight: 500,
      fontSize: 12.5,
      whiteSpace: 'nowrap'
    }}
  >
    <Box
      component='span'
      sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: 'currentColor' }}
    />
    {label}
  </Box>
);

export default DataTable;
